// src/pages/dashboard/Dashboard.js
// v5 FINAL — 30 May 2026

import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { retryOnPermissionDenied } from "../../utils/retryOnPermissionDenied";

import Header             from "../../components/Header";
import StatsCard          from "../../components/StatsCard";
import TransactionsTable  from "../../components/TransactionsTable";
import IncomeExpenseChart from "../../components/IncomeExpenseChart";
import ProfitDonutChart   from "../../components/ProfitDonutChart";

const parseDate = (val) => {
  if (!val) return null;
  if (typeof val.toDate === "function") return val.toDate();
  if (val instanceof Date) return val;
  if (typeof val === "string") return new Date(val);
  if (typeof val === "number") return new Date(val);
  return null;
};

export default function Dashboard() {
  const navigate  = useNavigate();
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user?.company_id || "";
  const role      = (user?.role || "owner").toLowerCase().trim();
  const myId      = user?.uid || user?.id || "";

  const isOwner      = role === "owner";
  const isSM         = role === "sm" || role === "manager";
  const isTechnician = role === "technician" || role === "tech";
  const isStore      = role === "store";
  const isAccountant = role === "accounts";
  const isFinance    = isOwner || isAccountant;

  const [kpis, setKpis] = useState({
    openServices: 0, inProgress: 0, pendingMRFs: 0,
    lowStockItems: 0, pendingInvoiceAmt: 0, pendingExpenseAmt: 0,
    thisMonthRevenue: 0, netPnL: 0, followUpToday: 0, toolsWithMe: 0,
  });
  const [tasks,        setTasks]        = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [stats,        setStats]        = useState({ income:0, expense:0, projects:0 });
  const [loading,      setLoading]      = useState(true);

  const fetchData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {

      // ── SERVICE TASKS ───────────────────────────────────
      let taskList = [];
      // Wrapped in retryOnPermissionDenied — this is the FIRST
      // company_id-scoped query fetchData() issues, so retrying it
      // covers the post-login claims race for every query below too
      // (12 Aug 2026 audit).
      if (isTechnician && myId) {
        const snap = await retryOnPermissionDenied(() => getDocs(query(
          collection(db, "service_tasks"),
          where("company_id", "==", companyId),
          where("status", "in", ["open","assigned","in_progress","partial","completed_pending_approval"])
        )));
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        taskList = all.filter(t =>
          t.assigned_to === myId ||
          (t.assigned_techs || []).some(tech => tech.id === myId)
        );
      } else {
        const snap = await retryOnPermissionDenied(() => getDocs(query(
          collection(db, "service_tasks"),
          where("company_id", "==", companyId)
        )));
        taskList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      }
      setTasks(taskList);

      const openServices = taskList.filter(t => ["open","assigned"].includes(t.status)).length;
      const inProgress   = taskList.filter(t => ["in_progress","partial"].includes(t.status)).length;

      // ── MRF (non-technician roles only) ─────────────────
      let pendingMRFs = 0;
      if (!isTechnician) {
        const mrfSnap = await getDocs(query(
          collection(db, "material_requests"),
          where("company_id", "==", companyId),
          where("status", "==", "pending")
        ));
        pendingMRFs = mrfSnap.size;
      }

      // ── TOOLS WITH ME (technician only) ─────────────────
      // Source: asset_master (cache, kept in sync by assetTransactionService)
      // Same pattern as Return Assets page — current_holder_id is the
      // canonical cache field, NOT a field on asset_transactions docs.
      let toolsWithMe = 0;
      if (isTechnician && myId) {
        const assetSnap = await getDocs(query(
          collection(db, "asset_master"),
          where("company_id",          "==", companyId),
          where("current_holder_id",   "==", myId)
        ));
        toolsWithMe = assetSnap.docs.filter(d => {
          const holderType = String(d.data().current_holder_type || "").toUpperCase();
          return ["STAFF", "TECHNICIAN"].includes(holderType);
        }).length;
      }

      // ── LOW STOCK ────────────────────────────────────────
      let lowStockItems = 0;
      if (isOwner || isSM || isStore) {
        const invSnap = await getDocs(query(
          collection(db, "inventory_summary"),
          where("company_id", "==", companyId)
        ));
        invSnap.forEach(d => {
          const item     = d.data();
          const stock    = Number(item.qty || item.current_stock || 0);
          const minStock = Number(item.minimum_stock || 0);
          if (minStock > 0 && stock <= minStock) lowStockItems++;
        });
      }

      // ── INVOICES + EXPENSES + TRANSACTIONS ───────────────
      let pendingInvoiceAmt  = 0;
      let thisMonthRevenue   = 0;
      let pendingExpenseAmt  = 0;   // declared once here
      let income             = 0;
      let expense            = 0;
      const txnData          = [];

      if (isFinance) {
        const now        = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

        // Invoices
        const invSnap = await getDocs(query(
          collection(db, "invoices"),
          where("company_id", "==", companyId)
        ));
        invSnap.forEach(d => {
          const inv    = d.data();
          const status = (inv.payment_status || "").toLowerCase();
          if (["pending","partial"].includes(status)) {
            pendingInvoiceAmt += Number(inv.balance_amount || inv.total_amount || 0);
          }
          const invDate = parseDate(inv.invoice_date) || parseDate(inv.created_at);
          if (invDate && invDate >= monthStart) {
            thisMonthRevenue += Number(inv.total_amount || 0);
          }
        });

        // Financial transactions
        const txnSnap = await getDocs(query(
          collection(db, "financial_transactions"),
          where("company_id", "==", companyId)
        ));
        txnSnap.forEach(d => {
          const tx = d.data();
          if (!tx.amount || Number(tx.amount) === 0) return;
          if (tx.type === "income")  income  += Number(tx.amount || 0);
          if (tx.type === "expense") expense += Number(tx.amount || 0);
          txnData.push({
            ...tx,
            date: parseDate(tx.created_at)?.toLocaleDateString("en-IN") || "-"
          });
        });

        // Pending expenses
        const expSnap = await getDocs(query(
          collection(db, "site_purchase_requests"),
          where("company_id", "==", companyId),
          where("status", "==", "pending")
        ));
        expSnap.forEach(d => { pendingExpenseAmt += Number(d.data().amount || 0); });
      }

      // ── FOLLOW-UPS TODAY ─────────────────────────────────
      let followUpToday = 0;
      if (isOwner || isSM) {
        const today = new Date().toISOString().slice(0, 10);
        const enqSnap = await getDocs(query(
          collection(db, "enquiries"),
          where("company_id", "==", companyId)
        ));
        enqSnap.forEach(d => {
          const e = d.data();
          if (e.follow_up_date === today &&
              !["Won", "Lost"].includes(e.status) &&
              !e.converted) {
            followUpToday++;
          }
        });
      }

      // ── PROJECTS ─────────────────────────────────────────
      const projectSnap = await getDocs(query(
        collection(db, "projects"),
        where("company_id", "==", companyId)
      ));

      setStats({ income, expense, projects: projectSnap.size });

      setTransactions(
        txnData
          .sort((a, b) => (parseDate(b.created_at)?.getTime()||0) - (parseDate(a.created_at)?.getTime()||0))
          .slice(0, 8)
      );

      setKpis({
        openServices, inProgress, pendingMRFs, lowStockItems,
        pendingInvoiceAmt, pendingExpenseAmt,
        thisMonthRevenue, netPnL: income - expense,
        followUpToday, toolsWithMe,
      });

    } catch (err) {
      console.error("Dashboard Error:", err);
    } finally {
      setLoading(false);
    }
  }, [companyId, myId, role]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

  return (
    <div className="p-4 md:p-6 space-y-6 bg-slate-50 min-h-screen">

      <Header />

      {/* TOP BAR */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-slate-800">
            {isOwner ? "📊 Owner Dashboard" : isSM ? "🛠 Service Manager"
             : isStore ? "📦 Store Dashboard" : isAccountant ? "💰 Accounts Dashboard"
             : "👷 My Dashboard"}
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            {new Date().toLocaleDateString("en-IN", { weekday:"long", day:"numeric", month:"long", year:"numeric" })}
          </p>
        </div>
        <button onClick={fetchData}
          className="text-xs text-slate-500 hover:text-slate-700 px-3 py-1.5 border border-slate-200 rounded-xl bg-white shadow-sm hover:bg-slate-50 transition">
          ↻ Refresh
        </button>
      </div>

      {/* SERVICE KPIs — Owner + SM */}
      {(isOwner || isSM) && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard icon="🔧" label="Active Services"  value={kpis.openServices}  hint="open + assigned"  color="blue"   onClick={() => navigate("/services")} />
            <KpiCard icon="⚡" label="In Progress"     value={kpis.inProgress}   hint="working + partial" color="amber"  onClick={() => navigate("/services")} />
            <KpiCard icon="📋" label="Pending MRFs"    value={kpis.pendingMRFs}  hint="awaiting approval" color="purple" onClick={() => navigate("/store-mrf")} />
            <KpiCard icon="📦" label="Low Stock"       value={kpis.lowStockItems} hint="below minimum"   color={kpis.lowStockItems > 0 ? "red" : "green"} onClick={() => navigate("/store-summary")} />
          </div>

          {/* FOLLOW-UP ALERT — shown only when there are follow-ups today */}
          {kpis.followUpToday > 0 && (
            <div
              onClick={() => navigate("/enquiries")}
              className="flex items-center gap-3 bg-amber-50 border border-amber-300 rounded-2xl px-4 py-3 cursor-pointer hover:bg-amber-100 transition">
              <span className="text-2xl">⏰</span>
              <div className="flex-1">
                <p className="text-sm font-bold text-amber-800">
                  {kpis.followUpToday} Follow-up{kpis.followUpToday > 1 ? "s" : ""} Due Today
                </p>
                <p className="text-xs text-amber-600">
                  Tap to view pending enquiry follow-ups
                </p>
              </div>
              <span className="text-amber-400 text-lg">→</span>
            </div>
          )}
        </>
      )}

      {/* FINANCIAL KPIs — Owner + Accountant */}
      {isFinance && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard icon="📅" label="This Month"       value={fmt(kpis.thisMonthRevenue)}  hint="revenue this month" color="green"  onClick={() => navigate("/billing/invoices")} />
          <KpiCard icon="🧾" label="Pending Invoices" value={fmt(kpis.pendingInvoiceAmt)} hint="unpaid balance"      color="orange" onClick={() => navigate("/billing/invoices")} />
          <KpiCard icon="💸" label="Pending Expenses" value={fmt(kpis.pendingExpenseAmt)} hint="awaiting approval"   color="red"    onClick={() => navigate("/expense-approval")} />
          <KpiCard icon="📈" label="Net P&L"          value={fmt(kpis.netPnL)}            hint="income − expense"    color={kpis.netPnL >= 0 ? "teal" : "red"} onClick={() => navigate("/reports/financial")} />
        </div>
      )}

      {/* STORE KPIs */}
      {isStore && (
        <div className="grid grid-cols-2 gap-3">
          <KpiCard icon="📋" label="Pending MRFs" value={kpis.pendingMRFs}    hint="awaiting issue" color="purple" onClick={() => navigate("/store-mrf")} />
          <KpiCard icon="📦" label="Low Stock"    value={kpis.lowStockItems}  hint="below minimum"  color={kpis.lowStockItems > 0 ? "red" : "green"} onClick={() => navigate("/store-summary")} />
        </div>
      )}

      {/* TECHNICIAN KPIs */}
      {isTechnician && (
        <div className="grid grid-cols-2 gap-3">
          <KpiCard icon="🔧" label="My Active Tasks" value={tasks.filter(t => !["closed","cancelled","completed"].includes(t.status)).length} hint="assigned to me" color="blue"   onClick={() => navigate("/technician")} />
          <KpiCard icon="🧰" label="Tools to Return" value={kpis.toolsWithMe}                                                                hint="currently with me" color="orange" onClick={() => navigate("/assets/returns")} />
        </div>
      )}

      {/* FINANCIAL SECTION — Owner + Accountant */}
      {isFinance && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { title:"Total Income",   value: fmt(stats.income),                color:"green" },
              { title:"Total Expenses", value: fmt(stats.expense),               color:"red"   },
              { title:"Projects",       value: stats.projects,                   color:"blue"  },
              { title:"Net Profit",     value: fmt(stats.income - stats.expense),
                color: stats.income >= stats.expense ? "green" : "red" },
            ].map((c, i) => <StatsCard key={i} title={c.title} value={c.value} color={c.color} />)}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
              <h3 className="font-semibold text-slate-700 mb-4">Income vs Expenses</h3>
              <IncomeExpenseChart />
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
              <h3 className="font-semibold text-slate-700 mb-4">Project Profitability</h3>
              <ProfitDonutChart />
            </div>
          </div>

          <TransactionsTable data={transactions} />
        </>
      )}

      {/* TASKS LIST */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
        <h3 className="font-semibold text-slate-700 mb-4">
          {isSM ? "📋 Team Tasks" : "📋 My Tasks"}
        </h3>
        {loading ? (
          <div className="space-y-2">
            {[1,2,3].map(i => <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />)}
          </div>
        ) : tasks.filter(t => !["closed","cancelled","completed"].includes(t.status) && t.complaint?.trim()).length === 0 ? (
          <div className="text-center py-8">
            <div className="text-3xl mb-2">✅</div>
            <p className="text-slate-400 text-sm">No active tasks</p>
          </div>
        ) : (
          <div className="space-y-2">
            {tasks
              .filter(t => !["closed","cancelled","completed"].includes(t.status) && t.complaint?.trim())
              .sort((a, b) => {
                const p = { in_progress:1, partial:2, assigned:3, completed_pending_approval:4, open:5 };
                return (p[a.status]||9) - (p[b.status]||9);
              })
              .map(task => {
                const S = {
                  open:                       { bg:"#EFF6FF", color:"#1D4ED8", label:"Open" },
                  assigned:                   { bg:"#DBEAFE", color:"#1E40AF", label:"Assigned" },
                  in_progress:                { bg:"#FEF9C3", color:"#854D0E", label:"In Progress" },
                  partial:                    { bg:"#FFEDD5", color:"#9A3412", label:"Partial" },
                  completed_pending_approval: { bg:"#EDE9FE", color:"#5B21B6", label:"Pending Approval" },
                };
                const ui = S[task.status] || { bg:"#F3F4F6", color:"#374151", label: task.status };
                return (
                  <div key={task.id} onClick={() => navigate(isTechnician ? `/tasks/${task.id}` : `/services/${task.id}`)}
                    className="flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:border-slate-200 hover:shadow-sm cursor-pointer transition bg-white"
                    style={{ borderLeft:`3px solid ${ui.color}` }}>
                    <div className="flex-1 min-w-0 mr-3">
                      <p className="text-sm font-medium text-slate-800 truncate">{task.complaint}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {[task.client_name, task.site_name].filter(Boolean).join(" • ")}
                        {task.scheduled_date && ` • 📅 ${task.scheduled_date}`}
                      </p>
                    </div>
                    <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full shrink-0"
                      style={{ background: ui.bg, color: ui.color }}>{ui.label}</span>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
}

const KPI_COLORS = {
  blue:   { bg:"bg-blue-500",    hover:"hover:bg-blue-600"   },
  amber:  { bg:"bg-amber-500",   hover:"hover:bg-amber-600"  },
  purple: { bg:"bg-purple-600",  hover:"hover:bg-purple-700" },
  green:  { bg:"bg-emerald-500", hover:"hover:bg-emerald-600"},
  red:    { bg:"bg-red-500",     hover:"hover:bg-red-600"    },
  orange: { bg:"bg-orange-500",  hover:"hover:bg-orange-600" },
  teal:   { bg:"bg-teal-600",    hover:"hover:bg-teal-700"   },
};

function KpiCard({ icon, label, value, hint, color="blue", onClick }) {
  const c = KPI_COLORS[color] || KPI_COLORS.blue;
  return (
    <button onClick={onClick}
      className={`${c.bg} ${c.hover} text-white p-4 rounded-2xl text-left w-full transition active:scale-95 shadow-sm`}>
      <div className="flex items-start justify-between mb-2">
        <span className="text-xl">{icon}</span>
        <span className="text-white/60 text-xs">→</span>
      </div>
      <p className="text-2xl font-bold leading-tight">{value}</p>
      <p className="text-xs font-semibold mt-1 text-white/90">{label}</p>
      {hint && <p className="text-[10px] text-white/60 mt-0.5">{hint}</p>}
    </button>
  );
}

function StatCard({ title, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
      <p className="text-xs text-slate-500 uppercase tracking-wide">{title}</p>
      <p className="text-xl font-bold mt-1 text-slate-800">{value}</p>
    </div>
  );
}